import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

const NAMES = {
  france: "FR", suisse: "CH", switzerland: "CH", schweiz: "CH", belgique: "BE", belgium: "BE", allemagne: "DE", germany: "DE", deutschland: "DE",
  espagne: "ES", spain: "ES", "españa": "ES", italie: "IT", italy: "IT", italia: "IT", luxembourg: "LU", "pays-bas": "NL", netherlands: "NL", nederland: "NL",
  "suède": "SE", suede: "SE", sweden: "SE", sverige: "SE", autriche: "AT", austria: "AT", "österreich": "AT", portugal: "PT", "royaume-uni": "GB", "united kingdom": "GB",
  danemark: "DK", denmark: "DK", "norvège": "NO", norway: "NO", pologne: "PL", poland: "PL", "république tchèque": "CZ", "tchéquie": "CZ", czechia: "CZ", "slovénie": "SI", slovenia: "SI",
};
export const countryCodeOf = (text, fallback = "FR") => {
  const s = (text || "").trim();
  if (/^[a-z]{2}$/i.test(s)) return s.toUpperCase();
  return NAMES[s.toLowerCase()] || (s ? "" : fallback);
};

const cache = new Map();
const lookup = async (cc, postal, signal) => {
  const key = `${cc}:${postal}`;
  if (cache.has(key)) return cache.get(key);
  const url = cc === "FR"
    ? `https://geo.api.gouv.fr/communes?codePostal=${encodeURIComponent(postal)}&fields=nom&format=json`
    : `https://api.zippopotam.us/${cc.toLowerCase()}/${encodeURIComponent(postal)}`;
  const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!res.ok) return [];
  const json = await res.json();
  const list = cc === "FR" ? (json || []).map((x) => x.nom) : (json.places || []).map((x) => x["place name"]);
  const uniq = [...new Set(list.filter(Boolean))];
  cache.set(key, uniq);
  return uniq;
};

// Champ ville : propose les communes du code postal (saisie manuelle toujours possible).
export const CityInput = ({ postal, countryCode, value, onChange, testid, className }) => {
  const [places, setPlaces] = useState([]);
  const [open, setOpen] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const cc = countryCode;
    const p = (postal || "").trim().toUpperCase();
    setPlaces([]);
    if (!cc || p.length < (cc === "FR" ? 5 : 3) || (cc === "FR" && !/^\d{5}$/.test(p))) return undefined;
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      const to = setTimeout(() => ctrl.abort(), 2500);
      try {
        const found = await lookup(cc, p, ctrl.signal);
        setPlaces(found);
        if (found.length === 1 && !(valueRef.current || "").trim()) onChange(found[0]);
        else if (found.length > 1 && !found.includes(valueRef.current)) setOpen(true);
      } catch { /* saisie manuelle */ } finally { clearTimeout(to); }
    }, 250);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [postal, countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative">
      <Input value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => places.length > 1 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} autoComplete="address-level2" data-testid={testid} className={className} />
      {open && places.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border bg-background shadow-lg" data-testid={`${testid}-suggestions`}>
          {places.map((c, i) => (
            <li key={c}>
              <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-muted" onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(c); setOpen(false); }} data-testid={`${testid}-option-${i}`}>{c}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
